import { type ShiftMigrationCounts } from "@clipboard-health/contract-backend-main";
import { formatShortMonthDayYear } from "@clipboard-health/date-time";
import { Box, Stack, type Theme } from "@mui/material";

import { Text } from "../../components/Text";
import {
  type ShiftMigrationDestinationWorkplace,
  type ShiftMigrationOriginWorkplace,
} from "../types";

interface ShiftMigrationPreviewStepProps {
  origin: ShiftMigrationOriginWorkplace;
  destination: ShiftMigrationDestinationWorkplace;
  effectiveStarting: Date;
  effectiveEnding?: Date;
  counts: ShiftMigrationCounts;
}

interface CountRowProps {
  label: string;
  value: number;
  emphasis?: boolean;
  indent?: boolean;
}

function CountRow(props: CountRowProps) {
  const { label, value, emphasis, indent } = props;
  const secondary = (theme: Theme) => (indent ? { color: theme.palette.text.secondary } : {});

  return (
    <>
      <Text semibold={emphasis} sx={[{ pl: indent ? 3 : 0 }, secondary]} variant="body2">
        {label}
      </Text>
      <Text semibold={emphasis} sx={[{ textAlign: "right" }, secondary]} variant="body2">
        {value}
      </Text>
    </>
  );
}

function CountGrid(props: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: `8rem max-content`,
        rowGap: 1,
        justifyContent: "start",
      }}
    >
      {props.children}
    </Box>
  );
}

export function ShiftMigrationPreviewStep(props: ShiftMigrationPreviewStepProps) {
  const { origin, destination, effectiveStarting, effectiveEnding, counts } = props;

  const ignoredShiftsMessage =
    counts.ignored.total > 0
      ? "Unverified shifts cannot be migrated unless they start more than 4 hours in the future."
      : "All shifts within the window are eligible to be migrated.";

  return (
    <Stack spacing={4}>
      <Stack spacing={1}>
        <Text variant="body2">
          Migrating shifts from <b>{origin.name}</b> to <b>{destination.name}</b>, starting{" "}
          <b>{formatShortMonthDayYear(effectiveStarting, { timeZone: origin.tmz })}</b>
          {effectiveEnding ? (
            <>
              {" "}
              through <b>{formatShortMonthDayYear(effectiveEnding, { timeZone: origin.tmz })}</b>
            </>
          ) : null}
          .
        </Text>
      </Stack>

      <Stack spacing={1}>
        <Text semibold variant="body1">
          Shifts to migrate
        </Text>
        <CountGrid>
          <CountRow emphasis label="Total migratable" value={counts.migratable.total} />
          <CountRow indent label="Verified" value={counts.migratable.verified} />
          <CountRow indent label="Booked" value={counts.migratable.booked} />
          <CountRow indent label="Open" value={counts.migratable.open} />
        </CountGrid>
      </Stack>

      <Stack spacing={1}>
        <Text semibold variant="body1">
          Shifts that will be ignored
        </Text>
        <CountGrid>
          <CountRow emphasis label="Total ignored" value={counts.ignored.total} />
        </CountGrid>
        <Text sx={(theme) => ({ color: theme.palette.text.secondary })} variant="caption">
          {ignoredShiftsMessage}
        </Text>
      </Stack>
    </Stack>
  );
}
